/**
 * @module features/client-device-management/components/EditAssignedDeviceDrawer
 * @description Responsive slide-over / mobile bottom-sheet drawer allowing Super Admins
 * to edit an assigned device's friendly name (`custom_device_name`), physical deployment
 * location (`location`), and operational notes (`notes`).
 */
import * as React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { AlertCircle, CheckCircle2, Cpu, Loader2 } from "lucide-react";
import { Drawer } from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AppError } from "@/lib/api-client";
import {
  AssignmentSummary,
  EditAssignmentFormSchema,
  EditAssignmentFormValues,
  formatDeviceTypeLabel,
} from "../types";
import { useUpdateAssignment } from "../api/assignmentApi";

export interface EditAssignedDeviceDrawerProps {
  assignment: AssignmentSummary | null;
  open: boolean;
  onClose: () => void;
  onSaved?: (updated: AssignmentSummary) => void;
  deviceTypes?: ReadonlyArray<{ type_id: string; label: string }>;
}

export function EditAssignedDeviceDrawer({
  assignment,
  open,
  onClose,
  onSaved,
  deviceTypes,
}: EditAssignedDeviceDrawerProps) {
  const updateMutation = useUpdateAssignment();
  const [submitError, setSubmitError] = React.useState<AppError | null>(null);
  const [savedNotice, setSavedNotice] = React.useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<EditAssignmentFormValues>({
    resolver: zodResolver(EditAssignmentFormSchema) as any,
    defaultValues: {
      custom_device_name: "",
      location: "",
      notes: "",
    },
  });

  const assignmentId = assignment?.id;

  React.useEffect(() => {
    if (assignment && open) {
      reset({
        custom_device_name: assignment.custom_device_name ?? "",
        location: assignment.location ?? "",
        notes: assignment.notes ?? "",
      });
      setSubmitError(null);
      setSavedNotice(false);
    }
    // Only reset notice/form state when opening the drawer or switching to a different assignment ID
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignmentId, open, reset]);

  const handleClose = (): void => {
    setSubmitError(null);
    setSavedNotice(false);
    onClose();
  };

  const isEditable = assignment?.status === "active";

  const onSubmit = async (values: EditAssignmentFormValues): Promise<void> => {
    if (!assignment || !isEditable) return;
    setSubmitError(null);
    setSavedNotice(false);
    try {
      const updated = await updateMutation.mutateAsync({
        assignmentId: assignment.id,
        input: {
          custom_device_name: values.custom_device_name ?? "",
          location: values.location ?? "",
          notes: values.notes ?? "",
        },
      });
      setSavedNotice(true);
      onSaved?.(updated);
    } catch (rawErr: unknown) {
      setSubmitError(
        AppError.fromUnknown(rawErr, "Failed to update assigned device details.")
      );
    }
  };

  const displayName = assignment
    ? assignment.custom_device_name || assignment.device_name
    : "Assigned Device";
  const gatewayTypeLabel = assignment
    ? formatDeviceTypeLabel(assignment.device_type, deviceTypes)
    : "";

  return (
    <Drawer
      open={open}
      onClose={handleClose}
      title={`Edit Assigned Device — ${displayName}`}
      description={
        assignment
          ? `${gatewayTypeLabel} · ${assignment.registered_device_id}`
          : undefined
      }
    >
      {assignment && (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5" noValidate>
          {/* Hardware Identity Summary (Immutable Central Registry Facts) */}
          <div className="rounded-xl border border-border/80 bg-muted/20 p-3.5 space-y-2">
            <div className="flex items-center gap-2">
              <Cpu
                className="h-4 w-4 text-muted-foreground shrink-0"
                aria-hidden="true"
              />
              <span className="text-xs font-semibold text-foreground">
                Hardware Identity
              </span>
              <span className="ml-auto inline-flex items-center rounded-full bg-muted px-2.5 py-0.5 text-[11px] font-medium text-foreground">
                {gatewayTypeLabel}
              </span>
            </div>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs pt-1">
              <div>
                <dt className="text-[11px] text-muted-foreground">Hardware Name</dt>
                <dd className="font-medium text-foreground truncate">
                  {assignment.device_name}
                </dd>
              </div>
              <div>
                <dt className="text-[11px] text-muted-foreground">Registered ID</dt>
                <dd className="font-mono text-foreground truncate">
                  {assignment.registered_device_id}
                </dd>
              </div>
              {assignment.mac_address && (
                <div className="sm:col-span-2">
                  <dt className="text-[11px] text-muted-foreground">MAC Address</dt>
                  <dd className="font-mono text-foreground">
                    {assignment.mac_address}
                  </dd>
                </div>
              )}
            </dl>
          </div>

          {!isEditable && (
            <div className="rounded-xl border border-warning/30 bg-warning-soft p-3 text-xs text-warning">
              This assignment is {assignment.status}; details are read-only for
              historical inspection.
            </div>
          )}

          {submitError && (
            <div
              role="alert"
              className="flex items-start gap-2 rounded-xl border border-danger/20 bg-danger-soft p-3 text-xs text-danger"
            >
              <AlertCircle
                className="h-4 w-4 shrink-0 mt-0.5"
                aria-hidden="true"
              />
              <div className="min-w-0">
                <p>{submitError.errorDetails}</p>
                {submitError.requestId && (
                  <p className="mt-1 font-mono text-[11px] text-muted-foreground break-all">
                    Request ID: {submitError.requestId}
                  </p>
                )}
              </div>
            </div>
          )}

          {savedNotice && (
            <div
              role="status"
              className="flex items-center gap-2 rounded-xl border border-success/20 bg-success-soft p-3 text-xs text-success"
            >
              <CheckCircle2 className="h-4 w-4 shrink-0" aria-hidden="true" />
              <span>Assigned device details updated.</span>
            </div>
          )}

          <fieldset disabled={!isEditable} className="space-y-4">
            <legend className="text-xs font-semibold text-foreground">
              Assignment Metadata
            </legend>

            <div className="space-y-1.5">
              <label
                htmlFor="edit-asgn-friendly-name"
                className="text-xs font-medium text-foreground block"
              >
                Device Friendly Name
              </label>
              <Input
                id="edit-asgn-friendly-name"
                autoComplete="off"
                maxLength={150}
                placeholder={assignment.device_name}
                {...register("custom_device_name")}
                className="h-11 sm:h-10 text-base sm:text-sm bg-card"
              />
              <p className="text-[11px] text-muted-foreground">
                Leave blank to fall back to the central hardware name (
                {assignment.device_name}).
              </p>
              {errors.custom_device_name && (
                <p role="alert" className="text-[11px] text-danger">
                  {errors.custom_device_name.message}
                </p>
              )}
            </div>

            <div className="space-y-1.5">
              <label
                htmlFor="edit-asgn-location"
                className="text-xs font-medium text-foreground block"
              >
                Deployment Location
              </label>
              <Input
                id="edit-asgn-location"
                autoComplete="off"
                maxLength={255}
                placeholder="e.g. Building A · Floor 2 Reception"
                {...register("location")}
                className="h-11 sm:h-10 text-base sm:text-sm bg-card"
              />
              {errors.location && (
                <p role="alert" className="text-[11px] text-danger">
                  {errors.location.message}
                </p>
              )}
            </div>

            <div className="space-y-1.5">
              <label
                htmlFor="edit-asgn-notes"
                className="text-xs font-medium text-foreground block"
              >
                Operational Notes
              </label>
              <textarea
                id="edit-asgn-notes"
                rows={3}
                maxLength={2000}
                placeholder="Deployment instructions, mounting details, or contact remarks..."
                {...register("notes")}
                className="w-full rounded-xl border border-input bg-card p-3 text-base sm:text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
              />
              {errors.notes && (
                <p role="alert" className="text-[11px] text-danger">
                  {errors.notes.message}
                </p>
              )}
            </div>
          </fieldset>

          <div className="pt-3 border-t border-border flex items-center justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              className="h-10 sm:h-9 touch:min-h-[44px] px-4 text-xs"
            >
              Close
            </Button>
            {isEditable && (
              <Button
                type="submit"
                disabled={updateMutation.isPending}
                className="h-10 sm:h-9 touch:min-h-[44px] px-4 text-xs min-w-[130px]"
              >
                {updateMutation.isPending ? (
                  <>
                    <Loader2
                      className="mr-2 h-4 w-4 animate-spin"
                      aria-hidden="true"
                    />
                    <span>Saving...</span>
                  </>
                ) : (
                  <span>Save Details</span>
                )}
              </Button>
            )}
          </div>
        </form>
      )}
    </Drawer>
  );
}
